import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
} from '@mui/material';

interface PITargetDisplayProps {
  values: {
    ideal_factor_a_min?: number;
    ideal_factor_a_max?: number;
    ideal_factor_b_min?: number;
    ideal_factor_b_max?: number;
    ideal_factor_c_min?: number;
    ideal_factor_c_max?: number;
    ideal_factor_d_min?: number;
    ideal_factor_d_max?: number;
    requires_a_greater_than_b?: boolean;
    requires_a_greater_than_c?: boolean;
    requires_a_greater_than_d?: boolean;
    requires_b_greater_than_a?: boolean;
    requires_b_greater_than_c?: boolean;
    requires_b_greater_than_d?: boolean;
    requires_c_greater_than_a?: boolean;
    requires_c_greater_than_b?: boolean;
    requires_c_greater_than_d?: boolean;
    requires_d_greater_than_a?: boolean;
    requires_d_greater_than_b?: boolean;
    requires_d_greater_than_c?: boolean;
  };
}

const factorInfo = {
  A: {
    name: 'Dominance',
    low: 'Collaborative',
    high: 'Independent',
    color: '#e53935', // red
  },
  B: {
    name: 'Extraversion',
    low: 'Reserved',
    high: 'Sociable',
    color: '#43a047', // green
  },
  C: {
    name: 'Patience',
    low: 'Driving',
    high: 'Steady',
    color: '#1e88e5', // blue
  },
  D: {
    name: 'Formality',
    low: 'Flexible',
    high: 'Precise',
    color: '#fb8c00', // orange
  },
};

// Convert between 0-100 scale and sigma scale (-3 to +3)
const toSigma = (value: number): number => {
  return (value - 50) / 16.67; // 50 = 0σ, each σ ≈ 16.67 units
};

const PITargetDisplay: React.FC<PITargetDisplayProps> = ({ values }) => {
  const renderFactorBar = (factor: 'A' | 'B' | 'C' | 'D') => {
    const info = factorInfo[factor];
    const min = values[`ideal_factor_${factor.toLowerCase()}_min` as keyof typeof values] as number || 0;
    const max = values[`ideal_factor_${factor.toLowerCase()}_max` as keyof typeof values] as number || 100;
    const sigmaMin = toSigma(min);
    const sigmaMax = toSigma(max);

    return (
      <Box key={factor} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body1" fontWeight="medium">
            Factor {factor} - {info.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sigmaMin.toFixed(1)}σ to {sigmaMax.toFixed(1)}σ
          </Typography>
        </Box>
        
        <Box sx={{ px: 1, mr: 1 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {info.low}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {info.high}
            </Typography>
          </Stack>
          
          <Box sx={{ position: 'relative', height: 40, mr: 1 }}>
            {/* Background scale */}
            <Box sx={{ 
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '100%',
              height: 2,
              backgroundColor: '#e0e0e0',
            }} />
            
            {/* Tick marks */}
            {[-3, -2, -1, 0, 1, 2, 3].map(sigma => (
              <Box
                key={sigma}
                sx={{
                  position: 'absolute',
                  left: `${(sigma + 3) * 16.67}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 1,
                  height: 8,
                  backgroundColor: '#bdbdbd',
                }}
              />
            ))}
            
            {/* Target range */}
            <Box sx={{ 
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: `${min}%`,
              width: `${max - min}%`,
              height: 8,
              backgroundColor: info.color,
              borderRadius: 4,
              opacity: 0.8,
            }} />
            
            {/* Sigma labels */}
            <Box sx={{ 
              position: 'absolute', 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'space-between',
              top: 25,
            }}>
              {[-3, -2, -1, 0, 1, 2, 3].map(sigma => (
                <Typography 
                  key={sigma} 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    left: `${(sigma + 3) * 16.67}%`,
                    transform: 'translateX(-50%)',
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    fontWeight: sigma === 0 ? 'bold' : 'normal'
                  }}
                >
                  {sigma === 0 ? '0' : sigma > 0 ? `+${sigma}` : sigma}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Collect active relationships
  const relationships: string[] = [];
  if (values.requires_a_greater_than_b) relationships.push('A > B');
  if (values.requires_a_greater_than_c) relationships.push('A > C');
  if (values.requires_a_greater_than_d) relationships.push('A > D');
  if (values.requires_b_greater_than_a) relationships.push('B > A');
  if (values.requires_b_greater_than_c) relationships.push('B > C');
  if (values.requires_b_greater_than_d) relationships.push('B > D');
  if (values.requires_c_greater_than_a) relationships.push('C > A');
  if (values.requires_c_greater_than_b) relationships.push('C > B');
  if (values.requires_c_greater_than_d) relationships.push('C > D');
  if (values.requires_d_greater_than_a) relationships.push('D > A');
  if (values.requires_d_greater_than_b) relationships.push('D > B');
  if (values.requires_d_greater_than_c) relationships.push('D > C');

  return (
    <Paper sx={{ p: 3, overflow: 'hidden' }}>
      <Typography variant="h6" gutterBottom>
        Ideal Candidate PI Profile
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        The behavioral target ranges for this position. Candidates whose PI factors fall within these ranges are likely to be a good behavioral fit.
      </Typography>

      <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        {['A', 'B', 'C', 'D'].map((factor) => 
          renderFactorBar(factor as 'A' | 'B' | 'C' | 'D')
        )}
      </Box>

      {relationships.length > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Required Factor Relationships:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {relationships.map((rel, index) => (
              <Chip
                key={index}
                label={rel}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default PITargetDisplay;